// CombatActionBar — the turn console. Appears above the composer when it's a
// human-controlled PC's turn in combat. Tabs for Attack / Cast / Features:
//  • Attack: weapon + target + advantage, two-handed, plus class riders that
//    appear conditionally (rogue → Sneak Attack, paladin → Divine Smite with a
//    slot-level select) and an off-hand bonus-action option.
//  • Cast: castable spells, a target multiselect (self / allies / foes) and an
//    upcast slot-level selector for leveled spells.
//  • Features: limited-use class features (Rage, Action Surge, Second Wind, …)
//    with remaining-uses and per-feature mode pickers where relevant.
// The action-economy pips grey out as the action/bonus/reaction are spent.
import { useEffect, useMemo, useState } from "react";
import { api } from "../lib/api";
import type {
  AuthUser,
  CombatFeature,
  CombatOptions,
  CombatSpellOption,
  FullCharacter,
  FullGame,
  Snapshot,
  WeaponAttackOption,
  WsClientMessage,
} from "../lib/types";
import { signed } from "../lib/format";

// A foe target shows its wound tier, never AC/exact HP (the player payload redacts
// those). Allies keep exact numbers. Falls back gracefully if a tier is absent.
function foeState(f: { wound_tier?: string; hp?: number; max_hp?: number }): string {
  if (f.wound_tier) {
    const t = f.wound_tier;
    return t.charAt(0).toUpperCase() + t.slice(1).replace(/-/g, " ");
  }
  if (f.hp != null && f.max_hp != null) return `${f.hp}/${f.max_hp}`;
  return "";
}

type Tab = "attack" | "cast" | "features";

// Sub-choices offered for features that take a `mode`.
const FEATURE_MODES: Record<string, string[]> = {
  "cunning-action": ["dash", "disengage", "hide"],
  "channel-divinity": ["turn-undead", "preserve-life", "invoke"],
};

export function CombatActionBar({
  gameId,
  game,
  snapshot,
  busy,
  send,
  you,
}: {
  gameId: string;
  game: FullGame;
  snapshot: Snapshot;
  busy: boolean;
  send: (m: WsClientMessage) => boolean;
  you: AuthUser | null;
}) {
  const combat = snapshot.combat;
  const [options, setOptions] = useState<CombatOptions | null>(null);
  // Distinguish "my turn but options still loading / failed" from "not my turn":
  // null = idle, "loading" = fetch in flight, "error" = fetch failed (offer retry).
  const [optState, setOptState] = useState<"loading" | "error" | null>(null);
  const [reloadKey, setReloadKey] = useState(0);
  const [character, setCharacter] = useState<FullCharacter | null>(null);
  const [tab, setTab] = useState<Tab>("attack");

  // Attack state
  const [weaponIdx, setWeaponIdx] = useState<string>("");
  const [targetId, setTargetId] = useState<string>("");
  const [adv, setAdv] = useState<"normal" | "advantage" | "disadvantage">("normal");
  const [twoHanded, setTwoHanded] = useState(false);
  const [offhand, setOffhand] = useState(false);
  const [sneak, setSneak] = useState(false);
  const [smiteLevel, setSmiteLevel] = useState<number>(0); // 0 = no smite

  // Cast state
  const [spellIdx, setSpellIdx] = useState<string>("");
  const [castTargets, setCastTargets] = useState<string[]>([]);
  const [slotLevel, setSlotLevel] = useState<number>(0);

  // Feature mode picks (feature_key → mode)
  const [featureMode, setFeatureMode] = useState<Record<string, string>>({});

  // The combatant whose turn it is. Match the seat by character_id (robust —
  // names can collide or differ from the seat label).
  const currentName = combat?.current_turn;
  const currentRow = combat?.order.find((c) => c.is_current);
  const currentCharId = currentRow?.character_id ?? null;
  const currentSeat = game.seats.find(
    (s) => s.character_id && s.character_id === currentCharId,
  );
  // Is the current combatant driven by a HUMAN seat (vs an AI seat or a monster)?
  const currentIsHumanControlled = !!currentRow?.is_pc && currentSeat?.kind === "human";
  // Do *I* own this seat? The active console is for the SEAT OWNER only — the
  // seat's claimant, or anyone for an unclaimed/legacy seat (solo/back-compat).
  // (The host's power to advance past a stuck human lives in the whose-turn bar
  // below, not here — the host does not pilot other players' consoles.)
  const iOwnCurrent =
    currentSeat?.kind === "human" &&
    (!currentSeat.user_id || (!!you && currentSeat.user_id === you.id));
  const myCharId = currentIsHumanControlled && iOwnCurrent ? currentCharId : null;
  const myCombatantId = currentRow?.id ?? null;
  const isMyTurn = !!(currentRow?.is_pc && myCharId && currentRow.alive);
  // The current actor is an AI/monster (no human at the wheel) — offer Advance.
  const currentIsNonHuman = !!currentRow && !currentIsHumanControlled;

  // Fetch combat options whenever the turn pointer / combat HP changes.
  useEffect(() => {
    if (!isMyTurn || !myCharId) { setOptions(null); setOptState(null); return; }
    let alive = true;
    setOptState("loading");
    api.combatOptions(gameId, myCharId)
      .then((o) => {
        if (!alive) return;
        setOptions(o);
        setOptState(null);
        const firstWeapon = o.weapons.find((w) => w.equipped) || o.weapons[0];
        setWeaponIdx((w) => (o.weapons.some((x) => x.index === w) ? w : firstWeapon?.index || ""));
        const firstFoe = o.foes.find((f) => f.alive) || o.foes[0];
        setTargetId((t) => (o.foes.some((f) => f.id === t && f.alive) ? t : firstFoe?.id || ""));
      })
      .catch(() => { if (alive) { setOptions(null); setOptState("error"); } });
    return () => { alive = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isMyTurn, myCharId, currentName, reloadKey, JSON.stringify(combat?.order.map((c) => [c.id, c.hp, c.is_current, c.used_action, c.used_bonus, c.used_reaction, c.movement_used]))]);

  // Fetch the full character once per character to read classes / slots (for riders).
  useEffect(() => {
    if (!isMyTurn || !myCharId) { setCharacter(null); return; }
    let alive = true;
    api.character(gameId, myCharId)
      .then((c) => alive && setCharacter(c))
      .catch(() => alive && setCharacter(null));
    return () => { alive = false; };
  }, [gameId, myCharId, isMyTurn]);

  const econ = options?.economy ?? null;
  const actionUsed = econ?.used_action ?? false;
  const bonusUsed = econ?.used_bonus ?? false;

  const weapon: WeaponAttackOption | undefined = options?.weapons.find((w) => w.index === weaponIdx);
  const liveFoes = useMemo(() => options?.foes.filter((f) => f.alive) ?? [], [options]);

  // ── class-rider detection ──
  const classes = character?.classes ?? [];
  const isRogue = classes.some((c) => c.class === "rogue");
  const isPaladin = classes.some((c) => c.class === "paladin");
  // Slot levels with an unspent slot (for Divine Smite + spell upcast).
  const availableSlotLevels = useMemo(() => {
    if (!character) return [] as number[];
    return Object.keys(character.spell_slots)
      .map(Number)
      .filter((L) => L >= 1 && (character.spell_slots[L] - (character.spell_slots_used[L] ?? 0)) > 0)
      .sort((a, b) => a - b);
  }, [character]);
  // Off-hand makes sense when a second melee weapon is on hand to two-weapon fight.
  const offhandPossible = (options?.weapons.filter((w) => !w.ranged).length ?? 0) >= 2;

  const spell: CombatSpellOption | undefined = options?.spells.find((s) => s.index === spellIdx);

  // Upcast options: only slot levels the caster actually has a slot for (at or
  // above the spell's base level). Offering a spent level would send that level
  // and let the backend silently auto-upcast into a higher slot — spending a
  // different resource than the UI showed.
  const upcastLevels = useMemo(() => {
    if (!spell || spell.level < 1) return [] as number[];
    return availableSlotLevels.filter((L) => L >= spell.level);
  }, [spell, availableSlotLevels]);

  // Keep the chosen slot level valid: cantrips → 0, leveled spells → the lowest
  // available slot (never a spent one). Runs when the spell or slot pool changes.
  useEffect(() => {
    if (!spell || spell.level < 1) { setSlotLevel(0); return; }
    setSlotLevel((cur) => (upcastLevels.includes(cur) ? cur : upcastLevels[0] ?? spell.level));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [spellIdx, upcastLevels.join(",")]);

  if (!combat) return null;

  // It IS my turn, but the options fetch is still loading or failed. Keep the
  // console mine — never fall through to a whose-turn/Advance bar that would let
  // me skip my own turn on a transient API error.
  if (isMyTurn && myCharId && !options) {
    return (
      <div className="combat-bar combat-bar--waiting">
        <div className="combat-bar__head">
          <span className="cb-turn">⚔️ Your turn — {currentName}</span>
        </div>
        <div className="cb-waiting-body">
          {optState === "error" ? (
            <>
              <span className="muted">Couldn't load your combat options.</span>
              <div className="grow" />
              <button className="btn btn--sm" disabled={busy}
                onClick={() => setReloadKey((k) => k + 1)}>↻ Retry</button>
            </>
          ) : (
            <span className="muted">Loading your options…</span>
          )}
        </div>
      </div>
    );
  }

  // Combat is active but it's NOT this client's turn — keep a visible combat console
  // so the UI clearly stays in "combat mode" every turn, rather than disappearing.
  // Advancing is free for AI/monster turns (they need a click to resolve), but
  // advancing past ANOTHER human permanently skips their action — only the host
  // may do that, and only behind a confirm.
  if (!isMyTurn || !myCharId || !options) {
    const canAdvanceHuman = currentIsNonHuman || !!you?.is_admin;
    const advance = () => {
      if (!currentIsNonHuman && !window.confirm(`Skip ${currentName}'s turn? Their action will be lost.`)) return;
      send({ action: "advance_combat" });
    };
    return (
      <div className="combat-bar combat-bar--waiting">
        <div className="combat-bar__head">
          <span className="cb-turn">⚔️ Combat — Round {combat.round}</span>
          <span className="cb-waiting-who">
            {currentRow ? <>Now: <b>{currentName}</b>{currentIsNonHuman ? " (NPC/monster)" : ""}</> : "Resolving…"}
          </span>
        </div>
        <div className="cb-waiting-body">
          {currentIsNonHuman
            ? <span className="muted">It's {currentName}'s turn. Advance to resolve their action.</span>
            : <span className="muted">Waiting for {currentName} to act…</span>}
          <div className="grow" />
          {canAdvanceHuman && (
            <button className="btn btn--sm" disabled={busy} onClick={advance}
              title={currentIsNonHuman ? "Resolve this NPC/monster turn" : "Host: skip this player's turn"}>
              {currentIsNonHuman ? "Advance turn →" : "Skip turn →"}
            </button>
          )}
        </div>
      </div>
    );
  }

  function toggleCastTarget(id: string) {
    setCastTargets((prev) => (prev.includes(id) ? prev.filter((t) => t !== id) : [...prev, id]));
  }

  function attack() {
    if (!weapon || !targetId || !myCharId) return;
    const msg: WsClientMessage = {
      action: "player_attack",
      character_id: myCharId,
      weapon_index: weapon.index,
      target_id: targetId,
      advantage: adv,
      two_handed: twoHanded,
      offhand,
    };
    if (isRogue && sneak) msg.sneak_attack = true;
    if (isPaladin && smiteLevel > 0) msg.smite_level = smiteLevel;
    send(msg);
    // One smite/sneak per strike — reset the one-shot riders.
    setSneak(false);
    setSmiteLevel(0);
  }

  function cast() {
    if (!spell || !myCharId) return;
    const msg: WsClientMessage = {
      action: "player_cast",
      character_id: myCharId,
      spell_index: spell.index,
      target_ids: castTargets,
    };
    if (spell.level >= 1 && slotLevel >= spell.level) msg.slot_level = slotLevel;
    send(msg);
    setCastTargets([]);
  }

  function useFeature(f: CombatFeature) {
    if (!myCharId) return;
    const msg: WsClientMessage = {
      action: "player_use_feature",
      character_id: myCharId,
      feature_key: f.key,
    };
    // Send the mode that's actually shown — the select displays modes[0] by
    // default, so send that even if the player never touched the dropdown.
    const mode = featureMode[f.key] ?? FEATURE_MODES[f.key]?.[0];
    if (mode) msg.mode = mode;
    send(msg);
  }

  // Gate a feature button by its action cost + remaining uses.
  function featureDisabled(f: CombatFeature): boolean {
    if (busy) return true;
    if (!f.at_will && f.remaining <= 0) return true;
    if (f.action === "bonus" && bonusUsed) return true;
    if (f.action === "action" && actionUsed) return true;
    // One reaction per round — an at-will reaction (e.g. Cutting Words) must still
    // lock out once the reaction is spent this round.
    if (f.action === "reaction" && (econ?.used_reaction ?? false)) return true;
    return false;
  }

  const attackOnBonus = offhand;
  const attackDisabled =
    busy || !weapon || !targetId || liveFoes.length === 0 ||
    (attackOnBonus ? bonusUsed : actionUsed);
  const castDisabled = busy || !spell || actionUsed;

  // ── self/ally/foe target pool for the Cast multiselect ──
  const selfRow = combat.order.find((c) => c.id === myCombatantId);
  const castPool: { id: string; label: string; side: string }[] = [];
  if (selfRow) castPool.push({ id: selfRow.id, label: `${selfRow.name} (you)`, side: "self" });
  options.allies.forEach((a) => castPool.push({ id: a.id, label: `${a.name} (${a.hp}/${a.max_hp})`, side: "ally" }));
  options.foes.filter((f) => f.alive).forEach((f) =>
    castPool.push({ id: f.id, label: `${f.name} · ${foeState(f)}`, side: "foe" }));

  const features = options.features;
  const activeEffects = options.active_effects;

  return (
    <div className="combat-bar">
      <div className="combat-bar__head">
        <span className="cb-turn">⚔️ Your turn — {currentName}</span>
        <div className="cb-head-right">
          {activeEffects.length > 0 && (
            <span className="cb-effects">
              {activeEffects.map((e) => (
                <span key={e} className="chip chip--effect" title="Active effect">🔥 {e}</span>
              ))}
            </span>
          )}
          {character?.concentration && (
            <span className="chip chip--conc" title="Concentrating">◎ {character.concentration}</span>
          )}
          {econ && (
            <span className="cb-economy">
              <span className={`econ-pip${econ.used_action ? " spent" : ""}`}>Action</span>
              <span className={`econ-pip${econ.used_bonus ? " spent" : ""}`}>Bonus</span>
              <span className={`econ-pip${econ.used_reaction ? " spent" : ""}`}>Reaction</span>
              <span className="econ-move">Move {econ.speed - econ.movement_used}/{econ.speed}ft</span>
            </span>
          )}
        </div>
      </div>

      <div className="cb-tabs">
        <button className={`cb-tab${tab === "attack" ? " on" : ""}`} onClick={() => setTab("attack")}>
          ⚔️ Attack
        </button>
        <button className={`cb-tab${tab === "cast" ? " on" : ""}`} onClick={() => setTab("cast")}
          disabled={options.spells.length === 0}>
          ✦ Cast{options.spells.length > 0 ? ` (${options.spells.length})` : ""}
        </button>
        <button className={`cb-tab${tab === "features" ? " on" : ""}`} onClick={() => setTab("features")}
          disabled={features.length === 0}>
          ★ Features{features.length > 0 ? ` (${features.length})` : ""}
        </button>
      </div>

      {/* ── ATTACK ── */}
      {tab === "attack" && (
        <div className="cb-section">
          <div className="combat-bar__row">
            <label className="cb-field">
              <span>Weapon</span>
              <select className="field" value={weaponIdx} onChange={(e) => setWeaponIdx(e.target.value)}>
                {options.weapons.map((w) => (
                  <option key={w.index} value={w.index}>
                    {w.name} ({signed(w.to_hit)}, {w.damage}{w.ranged ? ", ranged" : ""})
                  </option>
                ))}
              </select>
            </label>

            <label className="cb-field">
              <span>Target</span>
              <select className="field" value={targetId} onChange={(e) => setTargetId(e.target.value)}>
                {liveFoes.length === 0 && <option value="">No foes</option>}
                {liveFoes.map((f) => (
                  <option key={f.id} value={f.id}>{f.name} — {foeState(f)}</option>
                ))}
              </select>
            </label>

            <div className="adv-toggle cb-adv">
              <button className={adv === "disadvantage" ? "on dis" : ""} onClick={() => setAdv("disadvantage")} title="Disadvantage">Dis</button>
              <button className={adv === "normal" ? "on" : ""} onClick={() => setAdv("normal")}>Flat</button>
              <button className={adv === "advantage" ? "on adv" : ""} onClick={() => setAdv("advantage")} title="Advantage">Adv</button>
            </div>
          </div>

          <div className="cb-riders">
            {weapon?.two_handed_damage && (
              <label className="cb-checkbox">
                <input type="checkbox" checked={twoHanded} onChange={(e) => setTwoHanded(e.target.checked)} />
                Two-handed ({weapon.two_handed_damage})
              </label>
            )}
            {offhandPossible && (
              <label className="cb-checkbox" title="Two-weapon fighting — uses your bonus action">
                <input type="checkbox" checked={offhand} onChange={(e) => setOffhand(e.target.checked)} />
                Off-hand (bonus)
              </label>
            )}
            {isRogue && (
              <label className="cb-checkbox cb-rider" title="Add Sneak Attack dice on a hit (once per turn)">
                <input type="checkbox" checked={sneak} onChange={(e) => setSneak(e.target.checked)} />
                🗡 Sneak Attack
              </label>
            )}
            {isPaladin && (
              <label className="cb-checkbox cb-rider" title="Spend a spell slot to add radiant Divine Smite damage on a hit">
                <span>⚡ Divine Smite</span>
                <select className="field cb-mini" value={smiteLevel} onChange={(e) => setSmiteLevel(Number(e.target.value))}>
                  <option value={0}>off</option>
                  {availableSlotLevels.map((L) => (
                    <option key={L} value={L}>L{L} slot</option>
                  ))}
                </select>
              </label>
            )}
          </div>

          <div className="combat-bar__actions">
            <div className="grow" />
            <button className="btn btn--primary" disabled={attackDisabled} onClick={attack}
              title={attackDisabled && (attackOnBonus ? bonusUsed : actionUsed) ? "Action already used" : "Attack"}>
              {(attackOnBonus ? bonusUsed : actionUsed) ? "Action Used" : "⚔️ Strike"}
            </button>
          </div>
        </div>
      )}

      {/* ── CAST ── */}
      {tab === "cast" && (
        <div className="cb-section">
          {options.spells.length === 0 ? (
            <div className="cb-empty">No spells available to cast.</div>
          ) : (
            <>
              <div className="combat-bar__row">
                <label className="cb-field">
                  <span>Spell</span>
                  <select className="field" value={spellIdx} onChange={(e) => setSpellIdx(e.target.value)}>
                    <option value="">Choose a spell…</option>
                    {options.spells.map((s) => (
                      <option key={s.index} value={s.index}>
                        {s.name} {s.level === 0 ? "(cantrip)" : `(L${s.level})`}
                      </option>
                    ))}
                  </select>
                </label>
                {spell && spell.level >= 1 && upcastLevels.length > 0 && (
                  <label className="cb-field cb-slot">
                    <span>Slot level</span>
                    <select className="field" value={slotLevel} onChange={(e) => setSlotLevel(Number(e.target.value))}>
                      {upcastLevels.map((L) => (
                        <option key={L} value={L}>Level {L}{L > spell.level ? " (upcast)" : ""}</option>
                      ))}
                    </select>
                  </label>
                )}
              </div>

              {spell && (
                <div className="cb-targets">
                  <span className="cb-targets__label">Targets</span>
                  <div className="cb-targets__chips">
                    {castPool.length === 0 && <span className="cb-empty">No valid targets.</span>}
                    {castPool.map((t) => (
                      <button key={t.id}
                        className={`cb-target cb-target--${t.side}${castTargets.includes(t.id) ? " on" : ""}`}
                        onClick={() => toggleCastTarget(t.id)}>
                        {t.label}
                      </button>
                    ))}
                  </div>
                </div>
              )}

              <div className="combat-bar__actions">
                <div className="cb-cast-hint">
                  {spell ? `${castTargets.length} target${castTargets.length === 1 ? "" : "s"} selected` : "Pick a spell to cast"}
                </div>
                <div className="grow" />
                <button className="btn btn--primary" disabled={castDisabled} onClick={cast}
                  title={actionUsed ? "Action already used" : "Cast spell"}>
                  {actionUsed ? "Action Used" : `✦ Cast${spell ? ` ${spell.name}` : ""}`}
                </button>
              </div>
            </>
          )}
        </div>
      )}

      {/* ── FEATURES ── */}
      {tab === "features" && (
        <div className="cb-section">
          {features.length === 0 ? (
            <div className="cb-empty">No activated features available.</div>
          ) : (
            <div className="cb-feature-list">
              {features.map((f) => {
                const modes = FEATURE_MODES[f.key];
                return (
                  <div key={f.key} className="cb-feature">
                    <div className="cb-feature__info">
                      <div className="cb-feature__name">
                        {f.name}
                        <span className="cb-feature__uses">
                          {f.at_will ? "at-will" : `×${f.remaining}`}
                        </span>
                        <span className="cb-feature__action">{f.action}</span>
                      </div>
                      {f.desc && <div className="cb-feature__desc">{f.desc}</div>}
                    </div>
                    <div className="cb-feature__controls">
                      {modes && (
                        <select className="field cb-mini"
                          value={featureMode[f.key] ?? modes[0]}
                          onChange={(e) => setFeatureMode((m) => ({ ...m, [f.key]: e.target.value }))}>
                          {modes.map((m) => (
                            <option key={m} value={m}>{m}</option>
                          ))}
                        </select>
                      )}
                      <button className="btn btn--sm" disabled={featureDisabled(f)} onClick={() => useFeature(f)}>
                        Use
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}

      <div className="cb-footer">
        <div className="grow" />
        <button className="btn" disabled={busy}
          onClick={() => myCharId && send({ action: "player_end_turn", character_id: myCharId })}>
          End Turn →
        </button>
      </div>
    </div>
  );
}
